/**
 * Выдача предметов в сохранение (наборы, награды пропуска, ежедневные бонусы).
 * Если для предмета нет места на острове — вместо него начисляются монеты.
 * Монеты отсюда НЕ считаются «заработанными» (не влияют на рейтинг).
 */
import type { GameState } from './types';
import { buildingDef, animalDef, petDef, clothingDef } from './registry';
import { gainXp, gainFood, nextUid } from './player';
import { maxEnergy, ISLAND_TIERS } from './balance';
import { canPlace, islandGridSize } from './build';
import { findFreeGrass } from './island';
import { roll } from './rng';

export interface PackGrants {
  coins?: number;
  food?: number;
  xp?: number;
  fullEnergy?: boolean;
  rodMax?: boolean;
  islandTiers?: number;
  clothing?: string[];
  pets?: string[];
  animals?: string[];
  buildings?: string[];
  /** Открыть премиум-дорожку сезонного пропуска (обрабатывается в packs.ts). */
  passPremium?: boolean;
}

export interface PackResult {
  /** сколько монет реально начислено (включая компенсацию за предметы без места) */
  coins: number;
}

export function applyGrants(state: GameState, g: PackGrants, now: number, petName: (defId: string) => string): PackResult {
  let coins = g.coins ?? 0;
  const p = state.player;

  if (g.food) gainFood(state, g.food);
  if (g.xp) gainXp(state, g.xp);
  if (g.fullEnergy) { p.energy = maxEnergy(p.level); p.energyUpdatedAt = now; }
  if (g.rodMax) p.rodTier = 3;

  for (const id of g.clothing ?? []) {
    clothingDef(id);
    if (!p.wardrobe.includes(id)) p.wardrobe.push(id);
  }

  if (g.islandTiers) {
    const max = ISLAND_TIERS[ISLAND_TIERS.length - 1].tier;
    state.islandTier = Math.min(max, state.islandTier + g.islandTiers);
  }

  for (const id of g.pets ?? []) {
    const def = petDef(id);
    if (state.pets.some((x) => x.defId === id)) { coins += def.price; continue; }
    const spot = findFreeGrass(state, roll) ?? { x: 0, y: 0 };
    state.pets.push({ uid: nextUid(state), defId: id, name: petName(id), adoptedAt: now, fedAt: now, playedAt: 0, xp: 0, skillUsedAt: 0, x: spot.x, y: spot.y });
  }

  for (const id of g.animals ?? []) {
    const def = animalDef(id);
    const spot = findFreeGrass(state, roll);
    if (!spot) { coins += def.buyPrice; continue; }
    state.animals.push({ uid: nextUid(state), defId: id, x: spot.x, y: spot.y, fedAt: null });
  }

  for (const id of g.buildings ?? []) {
    const def = buildingDef(id);
    const spot = findBuildSpot(state, id);
    if (!spot) { coins += def.price; continue; }
    state.placements.push({ uid: nextUid(state), defId: id, x: spot.x, y: spot.y, rot: 0 });
  }

  p.coins += coins;
  return { coins };
}

/** Первое свободное место под здание (перебор сетки от центра). */
function findBuildSpot(state: GameState, defId: string): { x: number; y: number } | null {
  const n = islandGridSize(state), c = Math.floor(n / 2);
  const cells: { x: number; y: number; d: number }[] = [];
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) cells.push({ x, y, d: Math.abs(x - c) + Math.abs(y - c) });
  cells.sort((a, b) => a.d - b.d);
  for (const cell of cells) if (canPlace(state, defId, cell.x, cell.y)) return cell;
  return null;
}
